import {createContext, PropsWithChildren, useContext, useEffect, useState} from 'react';
import {useEventListener} from 'usehooks-ts';
import {IDevice, UAParser} from 'ua-parser-js';
import {noop} from '@utils/constants';

const breakpoints = {
    'bp360': '0',
    'bp768': '700px',
    'bp1024': '1000px',
    'bp1256': '1200px',
    'bp1342': '1300px',
    'bp1920': '1600px'
};

const removePX = (v: string) => parseInt(v.replace('px', ''));

export type Breakpoint = keyof typeof breakpoints;

export const BreakpointsContext = createContext<[Breakpoint, number]>(['bp360', removePX(breakpoints.bp360)]);

/**
 * вызывает fn при разрешении >= targetBp
 */
export const useBreakpointUp = (targetBp: Breakpoint, fn: () => any = noop) => {
    const [currentBpName, currentBpWidth] = useContext(BreakpointsContext);
    const [passed, setPassed] = useState(false);

    useEffect(() => {
        const targetBpWidth = removePX(breakpoints[targetBp]);

        if ((currentBpWidth >= targetBpWidth) && !passed) {
            fn();
            setPassed(true);
        }
        if ((currentBpWidth < targetBpWidth) && passed) {
            setPassed(false);
        }
    }, [currentBpName, passed]);

    return passed;
};

export const useDeviceType = (type: IDevice['type'] | IDevice['type'][], fn: (type: IDevice['type']) => any) => {
    useEffect(() => {
        if (!(type instanceof Array)) {
            type = [type];
        }
        const {device: {type: actualType}} = UAParser(navigator.userAgent);

        if (type?.indexOf(actualType) === -1) {
            return;
        }

        fn(actualType);

    }, [JSON.stringify(type)]);
};

export const BreakpointsProvider = ({children}: PropsWithChildren) => {
    const [current, setCurrent] = useState<[Breakpoint, number]>(['bp360', removePX(breakpoints.bp360)]);

    const handleResize = () => {
        const width = window.innerWidth;
        const [[newBpName]] = Object.entries(breakpoints)
            .map(([name, bpWidth]) => [name, width - removePX(bpWidth)] as [Breakpoint, number])
            .filter(([, w]) => w >= 0)
            .sort(([, w1], [, w2]) => w1 - w2);

        if (current[0] !== newBpName) {
            setCurrent([newBpName, removePX(breakpoints[newBpName])]);
        }
    };

    useEffect(handleResize, []);
    useEventListener('resize', handleResize);

    return <BreakpointsContext.Provider value={current}>
        {children}
    </BreakpointsContext.Provider>;
};